import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Grid3X3, List, Package, ChevronLeft, ChevronRight, MapPin, Search, X, Navigation, Loader2 } from "lucide-react";
import { ProductCardDetailed, ProductCardList } from "@/components/ProductCards";
import api from "@/utils/api";
import { toast } from "sonner";

const ITEMS_PER_PAGE = 24;

const RADIUS_OPTIONS = [
  { value: "10", label: "10 miles" },
  { value: "25", label: "25 miles" },
  { value: "50", label: "50 miles" },
  { value: "100", label: "100 miles" },
  { value: "250", label: "250 miles" },
  { value: "500", label: "500 miles" },
];

const BrowsePage = ({ userCurrency }) => {
  const [listings, setListings] = useState([]);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ category: "", subcategory: "", type: "", search: "", country: "", city: "" });
  const [locationSearch, setLocationSearch] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // grid or list
  const [currentPage, setCurrentPage] = useState(1);
  const [totalListings, setTotalListings] = useState(0);
  const [userCoords, setUserCoords] = useState(null); // { lat, lng }
  const [radius, setRadius] = useState(""); // in miles
  const [geoLoading, setGeoLoading] = useState(false);
  const [locationName, setLocationName] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const totalPages = Math.ceil(totalListings / ITEMS_PER_PAGE);
  const [filtersInitialized, setFiltersInitialized] = useState(false);

  // Fetch available countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await api.get("/listings/locations");
        setCountries(res.data.countries || []);
      } catch (error) {
        // Fallback to common countries
        setCountries([
          "United States", "Canada", "United Kingdom", "Germany", "France", 
          "Australia", "Japan", "Singapore", "India", "Netherlands"
        ]);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters = {
      category: params.get("category") || "",
      subcategory: params.get("subcategory") || "",
      type: params.get("type") || "",
      search: params.get("search") || "",
      country: params.get("country") || "",
      city: params.get("city") || ""
    };
    setFilters(newFilters);
    setLocationSearch(params.get("city") || "");
    
    // Handle geolocation params
    const lat = params.get("lat");
    const lng = params.get("lng");
    const rad = params.get("radius");
    const locName = params.get("locName");
    
    if (lat && lng) {
      setUserCoords({ lat: parseFloat(lat), lng: parseFloat(lng) });
      setRadius(rad || "50");
      setLocationName(locName || "Your Location");
    } else {
      setUserCoords(null);
      setRadius("");
      setLocationName("");
    }
    
    const page = parseInt(params.get("page")) || 1;
    setCurrentPage(page);
    setFiltersInitialized(true);
  }, [location.search]);

  useEffect(() => {
    if (!filtersInitialized) return;
    
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.category) params.set("category", filters.category);
        if (filters.subcategory) params.set("subcategory", filters.subcategory);
        if (filters.type) params.set("listing_type", filters.type);
        if (filters.search) params.set("search", filters.search);
        if (filters.country) params.set("country", filters.country);
        if (filters.city) params.set("city", filters.city);
        
        // Add geolocation params if available
        if (userCoords && radius) {
          params.set("lat", userCoords.lat.toString());
          params.set("lng", userCoords.lng.toString());
          // Convert miles to km for backend (backend expects km)
          const radiusKm = parseFloat(radius) * 1.60934;
          params.set("radius", radiusKm.toString());
        }
        
        params.set("limit", ITEMS_PER_PAGE.toString());
        params.set("skip", ((currentPage - 1) * ITEMS_PER_PAGE).toString());

        const [listingsRes, catRes] = await Promise.all([
          api.get(`/listings?${params.toString()}`),
          api.get("/categories")
        ]);
        setListings(listingsRes.data.listings);
        setTotalListings(listingsRes.data.total || listingsRes.data.listings.length);
        setCategories(catRes.data);
      } catch (error) {
        console.error("Failed to fetch listings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [filters, currentPage, filtersInitialized, userCoords, radius]);

  const updateFilter = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    params.delete("page"); // Reset to page 1 when filter changes
    navigate(`/browse?${params.toString()}`);
  };

  // Use browser geolocation
  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Try to get location name via reverse geocoding
        let locName = "Your Location";
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
          );
          const data = await response.json();
          if (data.address) {
            locName = data.address.city || data.address.town || data.address.village || 
                     data.address.county || data.address.state || "Your Location";
          }
        } catch (e) {
          console.log("Reverse geocoding failed, using default name");
        }

        // Update URL with geolocation params
        const params = new URLSearchParams(location.search);
        params.set("lat", latitude.toString());
        params.set("lng", longitude.toString());
        params.set("radius", "50"); // Default 50 miles
        params.set("locName", locName);
        params.delete("city"); // Clear city search when using geolocation
        params.delete("country"); // Clear country when using geolocation
        params.delete("page");
        
        setGeoLoading(false);
        toast.success(`Found items near ${locName}`);
        navigate(`/browse?${params.toString()}`);
      },
      (error) => {
        setGeoLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("Location access denied. Please enable location in your browser settings.");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("Location information unavailable.");
            break;
          case error.TIMEOUT:
            toast.error("Location request timed out.");
            break;
          default:
            toast.error("Unable to get your location.");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  };

  // Update radius and refetch
  const handleRadiusChange = (newRadius) => {
    if (!userCoords) return;
    
    const params = new URLSearchParams(location.search);
    params.set("radius", newRadius);
    params.delete("page");
    navigate(`/browse?${params.toString()}`);
  };

  // Clear geolocation filter
  const clearGeoFilter = () => {
    const params = new URLSearchParams(location.search);
    params.delete("lat");
    params.delete("lng");
    params.delete("radius");
    params.delete("locName");
    params.delete("page");
    navigate(`/browse?${params.toString()}`);
  };

  const handleLocationSearch = async (e) => {
    e.preventDefault();
    if (!locationSearch.trim()) return;
    
    const searchTerm = locationSearch.trim();
    setGeoLoading(true);
    
    try {
      // Geocode the city/zip code to get coordinates
      // Add "USA" to prioritize US locations for zip codes
      const isZipCode = /^\d{5}(-\d{4})?$/.test(searchTerm);
      const searchQuery = isZipCode ? `${searchTerm} USA` : searchTerm;
      
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchQuery)}&limit=1&countrycodes=us`
      );
      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon, display_name } = data[0];
        // Get city/area name from display_name
        const parts = display_name.split(',');
        const locationName = parts[0] || searchTerm;
        
        // Update URL with coordinates and radius
        const params = new URLSearchParams(location.search);
        params.set("lat", lat);
        params.set("lng", lon);
        params.set("radius", "50"); // Default 50 miles
        params.set("locName", locationName);
        params.set("city", searchTerm);
        params.delete("country");
        params.delete("page");
        
        setGeoLoading(false);
        toast.success(`Found location: ${locationName}`);
        navigate(`/browse?${params.toString()}`);
      } else {
        // Try without country restriction
        const fallbackResponse = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=1`
        );
        const fallbackData = await fallbackResponse.json();
        
        if (fallbackData && fallbackData.length > 0) {
          const { lat, lon, display_name } = fallbackData[0];
          const locationName = display_name.split(',')[0];
          
          const params = new URLSearchParams(location.search);
          params.set("lat", lat);
          params.set("lng", lon);
          params.set("radius", "50");
          params.set("locName", locationName);
          params.set("city", searchTerm);
          params.delete("country");
          params.delete("page");
          
          setGeoLoading(false);
          navigate(`/browse?${params.toString()}`);
        } else {
          // Fallback to text-based city search if geocoding fails
          setGeoLoading(false);
          updateFilter("city", searchTerm);
          toast.info(`Searching by name: ${searchTerm}`);
        }
      }
    } catch (error) {
      console.error("Geocoding failed:", error);
      setGeoLoading(false);
      updateFilter("city", searchTerm);
    }
  };

  const clearLocationFilter = () => {
    setLocationSearch("");
    const params = new URLSearchParams(location.search);
    params.delete("city");
    params.delete("country");
    params.delete("lat");
    params.delete("lng");
    params.delete("radius");
    params.delete("locName");
    params.delete("page");
    navigate(`/browse?${params.toString()}`);
  };

  const hasLocationFilter = filters.city || filters.country || userCoords;
  const hasGeoFilter = userCoords && radius;

  const goToPage = (page) => {
    const params = new URLSearchParams(location.search);
    if (page > 1) {
      params.set("page", page.toString());
    } else {
      params.delete("page");
    }
    navigate(`/browse?${params.toString()}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      if (currentPage > 3) {
        pages.push('...');
      }
      
      // Show pages around current page
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) {
          pages.push(i);
        }
      }
      
      if (currentPage < totalPages - 2) {
        pages.push('...');
      }
      
      // Always show last page
      if (!pages.includes(totalPages)) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <div className="min-h-screen pt-52 pb-16" data-testid="browse-page">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Filters Sidebar */}
          <aside className="w-full md:w-64 space-y-6">
            <Card className="p-4">
              <h3 className="font-semibold mb-4">Filters</h3>
              
              <div className="space-y-4">
                {/* Use My Location Button */}
                <div>
                  <Button
                    onClick={handleUseMyLocation}
                    disabled={geoLoading}
                    variant="outline"
                    className="w-full flex items-center justify-center gap-2 text-emerald-600 border-emerald-200 hover:bg-emerald-50 hover:border-emerald-300"
                    data-testid="use-my-location-btn"
                  >
                    {geoLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Navigation className="h-4 w-4" />
                    )}
                    {geoLoading ? "Finding location..." : "Use My Location"}
                  </Button>
                </div>

                {/* Geolocation Active - Show radius selector */}
                {hasGeoFilter && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-emerald-700 flex items-center gap-1">
                        <Navigation className="h-3 w-3" />
                        Near {locationName}
                      </span>
                      <button 
                        onClick={clearGeoFilter} 
                        className="text-emerald-600 hover:text-emerald-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                    <Label className="text-xs text-emerald-600 mb-1 block">Distance</Label>
                    <Select value={radius} onValueChange={handleRadiusChange}>
                      <SelectTrigger className="h-8 text-sm bg-white" data-testid="radius-filter">
                        <SelectValue placeholder="Select radius" />
                      </SelectTrigger>
                      <SelectContent>
                        {RADIUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Divider */}
                {!hasGeoFilter && (
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-muted-foreground">or search by</span>
                    </div>
                  </div>
                )}

                {/* Location Filter - only show if not using geolocation */}
                {!hasGeoFilter && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      City / Zip Code
                    </Label>
                    <form onSubmit={handleLocationSearch} className="flex gap-1">
                      <div className="relative flex-1">
                        <Input
                          type="text"
                          placeholder="Enter city or zip"
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          className="pr-8 text-sm"
                          data-testid="location-search-input"
                        />
                        {locationSearch && (
                          <button
                            type="button"
                            onClick={() => setLocationSearch("")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                      <Button type="submit" size="sm" variant="outline" className="px-2">
                        <Search className="h-4 w-4" />
                      </Button>
                    </form>
                    {filters.city && (
                      <div className="mt-2 flex items-center gap-1 text-xs text-emerald-600 bg-emerald-50 px-2 py-1 rounded">
                        <MapPin className="h-3 w-3" />
                        <span>Showing: {filters.city}</span>
                        <button onClick={clearLocationFilter} className="ml-auto hover:text-emerald-800">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Country Filter - only show if not using geolocation */}
                {!hasGeoFilter && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Country</Label>
                    <Select value={filters.country || "all"} onValueChange={(v) => updateFilter("country", v === "all" ? "" : v)}>
                      <SelectTrigger data-testid="country-filter">
                        <SelectValue placeholder="All Countries" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Countries</SelectItem>
                        {countries.map((country) => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Category</Label>
                  <Select value={filters.category || "all"} onValueChange={(v) => {
                    updateFilter("category", v === "all" ? "" : v);
                    updateFilter("subcategory", ""); // Clear subcategory when category changes
                  }}>
                    <SelectTrigger data-testid="category-filter">
                      <SelectValue placeholder="All Categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Categories</SelectItem>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Subcategory filter - shows when a category with subcategories is selected */}
                {filters.category && categories.find(c => c.id === filters.category)?.subcategories?.length > 0 && (
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Subcategory</Label>
                    <Select value={filters.subcategory || "all"} onValueChange={(v) => updateFilter("subcategory", v === "all" ? "" : v)}>
                      <SelectTrigger data-testid="subcategory-filter">
                        <SelectValue placeholder="All Subcategories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All {categories.find(c => c.id === filters.category)?.name}</SelectItem>
                        {categories.find(c => c.id === filters.category)?.subcategories?.map((sub) => (
                          <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Listing Type</Label>
                  <Select value={filters.type || "all"} onValueChange={(v) => updateFilter("type", v === "all" ? "" : v)}>
                    <SelectTrigger data-testid="type-filter">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="auction">Auctions</SelectItem>
                      <SelectItem value="fixed">Fixed Price</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {(filters.category || filters.subcategory || filters.type || filters.search || hasLocationFilter) && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      setLocationSearch("");
                      setUserCoords(null);
                      setRadius("");
                      setLocationName("");
                      navigate('/browse');
                    }}
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
            </Card>
          </aside>

          {/* Results */}
          <main className="flex-1">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="font-outfit text-2xl font-bold">
                  {filters.search ? `Results for "${filters.search}"` : 
                   hasGeoFilter ? `Items near ${locationName}` :
                   hasLocationFilter ? `Items in ${filters.city || filters.country}` : "All Items"}
                </h1>
                <p className="text-muted-foreground text-sm flex items-center gap-2 flex-wrap">
                  <span>{totalListings} items found</span>
                  {totalPages > 1 && <span>• Page {currentPage} of {totalPages}</span>}
                  {hasGeoFilter && (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs">
                      <Navigation className="h-3 w-3" />
                      Within {radius} miles
                    </span>
                  )}
                  {!hasGeoFilter && hasLocationFilter && (
                    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs">
                      <MapPin className="h-3 w-3" />
                      {filters.city && filters.country ? `${filters.city}, ${filters.country}` : 
                       filters.city || filters.country}
                    </span>
                  )}
                </p>
              </div>
              
              {/* View Toggle - Amazon style */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground hidden sm:block">View:</span>
                <div className="flex border rounded-lg overflow-hidden">
                  <button 
                    className={`p-2 ${viewMode === 'grid' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setViewMode('grid')}
                    data-testid="grid-view-btn"
                  >
                    <Grid3X3 className="h-4 w-4" />
                  </button>
                  <button 
                    className={`p-2 ${viewMode === 'list' ? 'bg-emerald-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    onClick={() => setViewMode('list')}
                    data-testid="list-view-btn"
                  >
                    <List className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>

            {loading ? (
              <div className={viewMode === 'grid' ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" : "space-y-4"}>
                {[1,2,3,4,5,6].map((i) => (
                  <Card key={i} className="animate-pulse">
                    <div className={viewMode === 'grid' ? "h-64 bg-muted rounded-t-xl" : "h-48 bg-muted"} />
                    <div className="p-4 space-y-3">
                      <div className="h-6 bg-muted rounded w-1/3" />
                      <div className="h-5 bg-muted rounded w-2/3" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : listings.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {listings.map((listing) => (
                      <ProductCardDetailed key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {listings.map((listing) => (
                      <ProductCardList key={listing.listing_id} listing={listing} userCurrency={userCurrency} />
                    ))}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8 pt-8 border-t">
                    {/* Previous Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="flex items-center gap-1"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="hidden sm:inline">Previous</span>
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex items-center gap-1">
                      {getPageNumbers().map((page, index) => (
                        page === '...' ? (
                          <span key={`ellipsis-${index}`} className="px-2 text-muted-foreground">...</span>
                        ) : (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => goToPage(page)}
                            className={`min-w-[40px] ${currentPage === page ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                          >
                            {page}
                          </Button>
                        )
                      ))}
                    </div>

                    {/* Next Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => goToPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="flex items-center gap-1"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Card className="p-12 text-center bg-slate-50 border-dashed">
                <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-semibold text-lg mb-2">No items found</h3>
                <p className="text-muted-foreground">Try adjusting your filters or search terms</p>
              </Card>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default BrowsePage;
